import { createApi } from '@reduxjs/toolkit/query/react';
import type {
  ApiResponse,
  GamePreferences,
  GameRoundHistory,
  GameRulesV2,
  InactivityConfig,
  LudoHistoryRow,
  UserRoundHistoryItems,
  UserRoundHistoryProps,
} from '@root/store/types';
import { baseQuery } from '@shared/store/baseQuery';

export const gameApi = createApi({
  reducerPath: 'gameApi',
  baseQuery,
  tagTypes: [
    'GamePreferences',
    'GameHistory',
    'GameRulesV2',
    'RoundHistory',
    'InactivityConfig',
  ],
  endpoints: (builder) => ({
    getGamePreferences: builder.query<
      ApiResponse<GamePreferences>,
      { userId: number; gameId: string }
    >({
      query: ({ userId, gameId }) => ({
        url: `api/player/v1/user/${userId}/game/${gameId}/preferences`,
        method: 'GET',
      }),
      providesTags: ['GamePreferences'],
    }),
    // The game's default stakes. Reset puts these back, not hardcoded values.
    getDefaultGamePreferences: builder.query<
      ApiResponse<GamePreferences>,
      { userId: number; gameId: string }
    >({
      query: ({ userId, gameId }) => ({
        url: `api/player/v1/user/${userId}/game/${gameId}/default/preferences`,
        method: 'GET',
      }),
    }),
    saveGamePreferences: builder.mutation<
      ApiResponse<GamePreferences>,
      GamePreferences
    >({
      query: (body) => ({
        url: `api/player/v1/user/${body.userId}/game/${body.gameId}/preferences`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['GamePreferences'],
    }),
    getGameHistory: builder.query<ApiResponse<GameRoundHistory>, string>({
      query: (gameId) => ({
        url: `api/player/v1/games/${gameId}/history`,
        method: 'GET',
      }),
      providesTags: ['GameHistory'],
    }),
    getGameRulesV2: builder.query<ApiResponse<GameRulesV2>, string>({
      query: (gameId) => ({
        url: `api/player/v2/games/${gameId}/rules`,
        method: 'GET',
      }),
      providesTags: ['GameRulesV2'],
    }),
    getUserRoundHistory: builder.query<
      ApiResponse<UserRoundHistoryItems>,
      UserRoundHistoryProps
    >({
      query: ({ userId, ...rest }) => ({
        url: `api/player/v2/user/${userId}/history`,
        method: 'GET',
        params: rest,
      }),
      providesTags: ['RoundHistory'],
    }),
    // One run's rolls in play order. Twelve tiles max, so no paging.
    getRunHistory: builder.query<
      ApiResponse<LudoHistoryRow[]>,
      { userId: number; journeyId: string }
    >({
      query: ({ userId, journeyId }) => ({
        url: `api/player/v1/user/${userId}/history/run/${journeyId}`,
        method: 'GET',
      }),
      providesTags: ['RoundHistory'],
    }),
    getGameInactivityConfig: builder.query<
      ApiResponse<InactivityConfig>,
      string
    >({
      query: (gameId) => ({
        url: `api/player/v1/game-view-configs/${gameId}`,
        method: 'GET',
      }),
      providesTags: ['InactivityConfig'],
    }),
  }),
});

export const {
  useGetGamePreferencesQuery,
  useLazyGetDefaultGamePreferencesQuery,
  useSaveGamePreferencesMutation,
  useGetGameHistoryQuery,
  useGetGameRulesV2Query,
  useGetUserRoundHistoryQuery,
  useGetRunHistoryQuery,
  useGetGameInactivityConfigQuery,
} = gameApi;
